import { Alert, Button, message } from "antd";
import React, { FC, useCallback, useMemo } from "react";
import { useToggle } from "@dewo/app/util/hooks";
import { LoginButton } from "../auth/buttons/LoginButton";
import { JoinTokenGatedWorkspacesModal } from "./JoinTokenGatedWorkspacesModal";
import { WorkspaceTokenGate } from "@dewo/app/graphql/types";
import { useJoinWorkspaceWithToken } from "./hooks";
import _ from "lodash";
import { UserProfileFormModal } from "../user/UserProfileFormModal";
import { useAuthContext } from "@dewo/app/contexts/AuthContext";
import { useOrganizationDetails } from "../organization/hooks";
import { LockOutlined } from "@ant-design/icons";

interface Props {
  organizationId: string;
}

export const JoinTokenGatedWorkspacesAlert: FC<Props> = ({
  organizationId,
}) => {
  const modalVisible = useToggle();
  const editingProfile = useToggle();
  const { user } = useAuthContext();
  const { organization, refetch } = useOrganizationDetails(organizationId);

  const tokenGates = useMemo(
    () =>
      organization?.workspaceTokenGates?.filter(
        (g) => !organization.workspaces.some((p) => p.id === g.workspaceId)
      ),
    [organization?.workspaceTokenGates, organization?.workspaces]
  );
  const tokens = useMemo(
    () =>
      _(tokenGates)
        .map((t) => t.token)
        .uniqBy((t) => t.id)
        .value(),
    [tokenGates]
  );

  const joinWorkspaceWithToken = useJoinWorkspaceWithToken();
  const verifyWorkspacesWithToken = useCallback(
    async (token: WorkspaceTokenGate["token"]) => {
      const workspaceIds =
        tokenGates
          ?.filter((t) => t.token.id === token.id)
          .map((t) => t.workspaceId) ?? [];
      try {
        for (const workspaceId of workspaceIds) {
          const workspace = await joinWorkspaceWithToken(workspaceId);
          message.success(`Joined ${workspace.name} using ${token.symbol}`);
        }
      } catch (error) {
        message.error("No workspaces to join using tokens in your wallet");
      } finally {
        await refetch();
        modalVisible.toggleOff();
      }
    },
    [tokenGates, refetch, joinWorkspaceWithToken, modalVisible]
  );

  const handleProfileSaved = useCallback(() => {
    modalVisible.toggleOn();
    editingProfile.toggleOff();
  }, [modalVisible, editingProfile]);

  if (!tokenGates?.length) return null;

  const tokensString = tokens.map((t) => t.symbol).join(", ");
  return (
    <>
      <Alert
        message={`There are private Spaces that you can access by authenticating using ${tokensString}`}
        type="info"
        description={
          !!user ? (
            <Button
              type="primary"
              icon={<LockOutlined />}
              onClick={modalVisible.toggleOn}
            >
              Join
            </Button>
          ) : (
            <LoginButton
              type="primary"
              icon={<LockOutlined />}
              onAuthedWithWallet={editingProfile.toggleOn}
            >
              Join
            </LoginButton>
          )
        }
        showIcon
      />
      {!!user && (
        <JoinTokenGatedWorkspacesModal
          tokens={tokens}
          visible={modalVisible.isOn}
          onClose={modalVisible.toggleOff}
          onVerify={verifyWorkspacesWithToken}
        />
      )}
      {!!user && (
        <UserProfileFormModal
          visible={editingProfile.isOn}
          onDone={handleProfileSaved}
        />
      )}
    </>
  );
};
